import { useState } from 'react'
import { Wallet, CalendarClock, Banknote, Clock, Bell, Send, CalendarPlus } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import type { VistaSaldoCliente } from '@/types/database'
import { formatCurrency, formatDate, obtenerPeriodoActual } from '@/lib/utils'
import { useCuentaCorrienteCliente } from '@/hooks/useCuentaCorriente'
import { useGenerarMovimientosEnLote, usePrevisualizacionCliente } from '@/hooks/useMovimientos'
import { usePermissions } from '@/hooks/usePermissions'
import { LoteConcurrenteError } from '@/services/honorariosService'
import { LineaDeTiempoCliente } from './LineaDeTiempoCliente'

interface CuentaCorrienteTabProps {
  cliente: VistaSaldoCliente
  /** Eleva al padre la acción de enviar estado de cuenta (evita Sheet anidado). */
  onEnviarEstadoCuenta: () => void
  /** La solapa no la usa. */
  onVerVencimientos: () => void
}

export function CuentaCorrienteTab({ cliente, onEnviarEstadoCuenta }: CuentaCorrienteTabProps) {
  const clienteId = cliente.id ?? ''
  const { data: resumen, isLoading: loadingResumen } = useCuentaCorrienteCliente(clienteId)

  const { puede } = usePermissions()
  const [confirmando, setConfirmando] = useState(false)
  const generarLote = useGenerarMovimientosEnLote()
  // Mismos valores por defecto que Movimientos > Generar lote: mes actual, concepto estándar y hoy.
  const periodo = obtenerPeriodoActual()
  // Generar la deuda usa el precio de cada servicio: pide crearMovimiento y verHonorariosCliente.
  const puedeGenerarDeuda = puede.crearMovimiento === true && puede.verHonorariosCliente === true
  const {
    data: previsualizacion,
    isLoading: loadingPrevisualizacion,
    isError: falloPrevisualizacion,
    refetch: reintentarPrevisualizacion,
  } = usePrevisualizacionCliente(clienteId, periodo, puedeGenerarDeuda)
  const hayDeudaParaGenerar = previsualizacion?.estado === 'creado'
  // Cargo no mensual del mismo mes y monto: la confirmación lo muestra y pide cobrarlo igual.
  const posibleDuplicado = previsualizacion?.posibleDuplicado
  // Sin la vista previa no se sabe si la deuda existe: el motivo no puede afirmar que ya está generada.
  const motivoSinDeuda = falloPrevisualizacion
    ? 'No se pudo calcular la deuda del mes'
    : previsualizacion?.razon === 'Sin servicios que correspondan al período'
      ? 'Este cliente no tiene servicios mensuales que correspondan a este mes'
      : previsualizacion?.razon === 'Sin honorarios'
        ? 'Los servicios de este mes no tienen precio cargado'
        : 'La deuda de este mes ya está generada'

  const handleGenerarDeuda = async () => {
    setConfirmando(false)
    const hoy = new Date()
    const fecha = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`
    try {
      const resultado = await generarLote.mutateAsync({
        mesAnio: periodo,
        concepto: 'Honorarios {servicio} - {periodo}',
        fecha,
        clienteIds: [clienteId],
        loteCompleto: false,
      })
      toast.success(`Se generó la deuda de ${periodo} por ${formatCurrency(resultado.montoTotal)}`)
    } catch (error) {
      toast.error(error instanceof LoteConcurrenteError ? error.message : 'No se pudo generar la deuda del mes')
      console.error(error)
    }
  }

  const saldo = cliente.saldo ?? 0
  const mesesDeuda = resumen?.mesesDeuda ?? 0

  return (
    <div className="px-6 py-4 space-y-6">
      {/* Métricas financieras */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className={`rounded-lg border p-3 ${
          saldo > 0
            ? 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800'
            : 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800'
        }`}>
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <Wallet className="h-4 w-4" />
            <span className="text-xs uppercase tracking-wide">Saldo</span>
          </div>
          <p className={`text-lg font-bold tabular-nums ${saldo > 0 ? 'text-red-600' : 'text-green-600'}`}>
            {formatCurrency(Math.abs(saldo))}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {saldo > 0 ? 'a cobrar' : saldo < 0 ? 'a favor' : 'sin saldo'}
          </p>
        </div>

        <div className="rounded-lg border p-3">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <CalendarClock className="h-4 w-4" />
            <span className="text-xs uppercase tracking-wide">Antigüedad</span>
          </div>
          <p className="text-lg font-bold tabular-nums">
            {loadingResumen ? '—' : mesesDeuda > 0 ? `${Math.round(mesesDeuda)} m` : 'Al día'}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {resumen?.deudaMasAntigua ? `desde ${formatDate(resumen.deudaMasAntigua)}` : 'sin deuda antigua'}
          </p>
        </div>

        <div className="rounded-lg border p-3">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <Banknote className="h-4 w-4" />
            <span className="text-xs uppercase tracking-wide">Último pago</span>
          </div>
          <p className="text-lg font-bold tabular-nums">
            {loadingResumen ? '—' : resumen?.ultimoPago ? formatDate(resumen.ultimoPago) : 'Sin pagos'}
          </p>
        </div>

        <div className="rounded-lg border p-3">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <Clock className="h-4 w-4" />
            <span className="text-xs uppercase tracking-wide">Días sin pago</span>
          </div>
          <p className={`text-lg font-bold tabular-nums ${
            (resumen?.diasSinPago ?? 0) > 60 ? 'text-red-600' : ''
          }`}>
            {loadingResumen ? '—' : resumen?.diasSinPago != null ? resumen.diasSinPago : '—'}
          </p>
        </div>
      </div>

      {/* Última notificación de deuda + acción enviar */}
      <div className="rounded-lg border bg-muted/30 p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-muted-foreground shrink-0" />
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Última notificación de deuda</p>
              <p className="font-medium">
                {loadingResumen
                  ? '—'
                  : resumen?.ultimaNotificacion
                    ? formatDate(resumen.ultimaNotificacion)
                    : 'Nunca notificado'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {puedeGenerarDeuda && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={hayDeudaParaGenerar ? undefined : 0}>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!hayDeudaParaGenerar || generarLote.isPending}
                        onClick={() => setConfirmando(true)}
                      >
                        <CalendarPlus className="h-4 w-4 mr-2" />
                        Generar deuda del mes
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {!loadingPrevisualizacion && !hayDeudaParaGenerar && (
                    <TooltipContent>{motivoSinDeuda}</TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            )}
            {puedeGenerarDeuda && falloPrevisualizacion && (
              <>
                <span className="text-sm text-destructive">{motivoSinDeuda}</span>
                <Button size="sm" variant="ghost" onClick={() => void reintentarPrevisualizacion()}>
                  Reintentar
                </Button>
              </>
            )}
            <Button size="sm" onClick={onEnviarEstadoCuenta}>
              <Send className="h-4 w-4 mr-2" />
              Enviar estado de cuenta
            </Button>
          </div>
        </div>
      </div>

      <AlertDialog open={confirmando} onOpenChange={setConfirmando}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Generar la deuda de {periodo}</AlertDialogTitle>
            <AlertDialogDescription>
              Se va a cargar una deuda de {formatCurrency(previsualizacion?.monto ?? 0)} a {cliente.nombre ?? 'este cliente'}.
            </AlertDialogDescription>
            {posibleDuplicado && (
              <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
                Ya tiene un cargo manual de {formatCurrency(posibleDuplicado.monto)} en este mes ({posibleDuplicado.servicio}). Confirmá solo si corresponde cobrarlo igual.
              </p>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleGenerarDeuda}>{posibleDuplicado ? 'Cobrar igual' : 'Generar deuda'}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <LineaDeTiempoCliente clienteId={clienteId} />
    </div>
  )
}
